import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { AlertCircle, ChevronDown, FileText, Loader } from 'lucide-react';
import { partnersApi } from '../../services/partnersApi';
import { EmptyState } from '../ui/EmptyState';
import { SkeletonLoader } from '../SkeletonLoader';
import { PartnerReportDetail } from './PartnerReportDetail';
import { PartnerUpdateStatusControl } from './PartnerUpdateStatusControl';
import { ReportSourceBadge, ReportStatusBadge } from './PartnerPhaseBadge';
import type { PartnerPack, PartnerUpdatesParams, ReportView } from '../../types/partners';
import { cn } from '../../utils/cn';
import { partnerPagePath } from '../../utils/partnerLinks';
import type { PartnerNotice } from '../../utils/partnerSync';

interface PartnerReportListProps {
  /** Which reports: status, partner, pack, source. */
  query: Omit<PartnerUpdatesParams, 'cursor' | 'limit'>;
  /** Show which partner each report is from (the inbox); not needed on a partner's own page. */
  showPartner?: boolean;
  canEdit: boolean;
  /** The report to open, e.g. from `?report=`; it is fetched if the filter hides it. */
  focusId?: string | null;
  onFocusChange?: (id: string | null) => void;
  /** The partner's packs, when the list is on their page. */
  packs?: PartnerPack[];
  /** The viewer can open Modpack Configs, for "Update the cluster". */
  canOpenConfigs?: boolean;
  onNotice: (notice: PartnerNotice) => void;
  /** Bump to reload, e.g. after logging a report. */
  refreshKey?: number;
  /** Told whenever a report changes, so counts elsewhere can refresh. */
  onChanged?: () => void;
  emptyTitle?: string;
  emptyDescription?: string;
  pageSize?: number;
}

/**
 * Update reports, newest first, each opening in place to its full detail.
 * Staff can move a report on straight from its row.
 */
export const PartnerReportList = ({
  query,
  showPartner = false,
  canEdit,
  focusId,
  onFocusChange,
  packs,
  canOpenConfigs = false,
  onNotice,
  refreshKey = 0,
  onChanged,
  emptyTitle = 'No update reports',
  emptyDescription,
  pageSize = 25,
}: PartnerReportListProps) => {
  const [reports, setReports] = useState<ReportView[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localOpen, setLocalOpen] = useState<string | null>(focusId ?? null);
  const open = onFocusChange ? focusId ?? null : localOpen;
  const setOpen = onFocusChange ?? setLocalOpen;
  const focusRef = useRef<HTMLLIElement | null>(null);
  const scrolledTo = useRef<string | null>(null);

  const queryKey = JSON.stringify(query);
  // Read when the list loads, so opening another row does not refetch the list.
  const focusIdRef = useRef(focusId);
  focusIdRef.current = focusId;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const page = await partnersApi.updates({ ...(JSON.parse(queryKey) as PartnerUpdatesParams), limit: pageSize });
      let list = page.reports;
      // A link to one report opens it even when the current filter would hide it.
      const wanted = focusIdRef.current;
      if (wanted && !list.some((r) => r.id === wanted)) {
        try {
          const { report } = await partnersApi.report(wanted);
          list = [report, ...list];
        } catch {
          // The report may have been deleted; the list still shows.
        }
      }
      setReports(list);
      setCursor(page.next_cursor);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The update reports could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [queryKey, pageSize]);

  // refreshKey is a request to load again, e.g. after logging a report.
  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  useEffect(() => {
    if (!loading && focusId && scrolledTo.current !== focusId && focusRef.current) {
      scrolledTo.current = focusId;
      focusRef.current.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  }, [loading, focusId]);

  const more = async () => {
    if (!cursor) return;
    setLoadingMore(true);
    try {
      const page = await partnersApi.updates({ ...query, limit: pageSize, cursor });
      setReports((prev) => [...prev, ...page.reports.filter((r) => !prev.some((p) => p.id === r.id))]);
      setCursor(page.next_cursor);
    } catch (e) {
      onNotice({ tone: 'danger', text: e instanceof Error ? e.message : 'More reports could not be loaded.' });
    } finally {
      setLoadingMore(false);
    }
  };

  const replace = (report: ReportView) => {
    setReports((prev) => prev.map((r) => (r.id === report.id ? report : r)));
    onChanged?.();
  };

  if (loading && reports.length === 0) {
    return (
      <div className="space-y-2" aria-busy="true">
        {Array.from({ length: 4 }).map((_, i) => (
          <SkeletonLoader key={i} className="h-14" />
        ))}
      </div>
    );
  }

  if (error && reports.length === 0) {
    return (
      <EmptyState
        bare
        compact
        tone="danger"
        icon={AlertCircle}
        title="The update reports could not be loaded"
        description={error}
        action={
          <button type="button" className="glass-button" onClick={() => void load()}>
            Retry
          </button>
        }
      />
    );
  }

  if (reports.length === 0) {
    return <EmptyState bare compact icon={FileText} title={emptyTitle} description={emptyDescription} />;
  }

  return (
    <div className="space-y-3">
      <ul className="rounded-lg border border-glass-200/10 divide-y divide-glass-200/10 overflow-hidden">
        {reports.map((report) => {
          const isOpen = open === report.id;
          return (
            <li key={report.id} ref={report.id === focusId ? focusRef : undefined} className={cn(isOpen && 'bg-accent-500/5')}>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2.5">
                <div className="flex-1 min-w-[14rem] flex items-start gap-2.5">
                  <button
                    type="button"
                    className="btn-icon btn-icon-sm -ml-1 shrink-0"
                    onClick={() => setOpen(isOpen ? null : report.id)}
                    aria-expanded={isOpen}
                    title={isOpen ? 'Hide the details' : 'Show the details'}
                    aria-label={isOpen ? `Hide the details of ${report.pack_name} ${report.version}` : `Show the details of ${report.pack_name} ${report.version}`}
                  >
                    <ChevronDown className={cn('w-4 h-4 transition-transform', isOpen && 'rotate-180')} />
                  </button>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <button type="button" className="text-left hover:text-accent-300 transition-colors" onClick={() => setOpen(isOpen ? null : report.id)}>
                        <span className="text-sm font-medium text-white">{report.pack_name}</span>{' '}
                        <span className="font-mono text-sm text-accent-300">{report.version}</span>
                      </button>
                      {report.release_state === 'upcoming' && <span className="chip">Coming soon</span>}
                      <ReportSourceBadge source={report.source} />
                    </div>
                    <p className="text-xs text-glass-500 mt-0.5">
                      {showPartner && (
                        <>
                          <Link to={partnerPagePath(report.creator_id)} className="link">
                            {report.creator_name}
                          </Link>
                          {' · '}
                        </>
                      )}
                      {report.source === 'curseforge' ? 'Listed' : 'Reported'} {formatDistanceToNow(new Date(report.created_at), { addSuffix: true })}
                      {report.reporter && report.source !== 'curseforge' ? ` by ${report.reporter.name}` : ''}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {canEdit ? (
                    <PartnerUpdateStatusControl report={report} compact onSaved={replace} onNotice={onNotice} />
                  ) : (
                    <ReportStatusBadge status={report.status} />
                  )}
                </div>
              </div>
              {isOpen && (
                <div className="px-3 pb-3 md:px-4 md:pb-4 border-t border-glass-200/10 pt-3">
                  <PartnerReportDetail report={report} packs={packs} canEdit={canEdit} canOpenConfigs={canOpenConfigs} onSaved={replace} onNotice={onNotice} />
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {cursor && (
        <button type="button" className="glass-button-secondary" onClick={() => void more()} disabled={loadingMore}>
          {loadingMore && <Loader className="w-4 h-4 animate-spin" />}
          Show older reports
        </button>
      )}
    </div>
  );
};
